<template>
  <div class="page page-day-report">
    <!-- Top Bar with Logo and Download Button (same as HomeUserCenter) -->
    <div class="custom-top-bar">
      <div class="brand-area">
        <img :src="icons.logo" class="brand-logo" alt="logo" />
        <span class="brand-text">Kukukao</span>
      </div>
      <button class="btn-download" @click="handleOpenApp">下载app</button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <div class="loading-text">加载中...</div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-text">{{ error }}</div>
      <button class="btn-retry" @click="fetchDayReport">重试</button>
    </div>

    <div v-else class="content-wrapper">
      <!-- Yellow Gradient Header -->
      <div class="header-bg"></div>

      <div class="phone">
        <div class="phone-inner">
          <!-- Header Content -->
          <div class="report-header">
            <div class="header-content">
              <div class="header-item">
                <div class="label">
                  <img :src="icons.spark" class="header-icon" alt="xp" />
                  今日经验值
                </div>
                <div class="value large">+{{ reportData.todayExp || 0 }}</div>
              </div>
              <div class="divider-vertical"></div>
              <div class="header-item">
                <div class="label">
                  <img :src="icons.streakFlame" class="header-icon" alt="streak" />
                  连续打卡
                </div>
                <div class="value">{{ reportData.signCount || 0 }}</div>
              </div>
            </div>
          </div>

          <!-- Floating Stats Card -->
          <div class="stats-card">
             <div class="stat-col">
                <img :src="icons.time" class="stat-icon-img" alt="time" />
                <div class="stat-val">{{ reportData.studyDuration || 0 }}<span class="unit">分钟</span></div>
                <div class="stat-label">学习时长</div>
              </div>
              <div class="stat-col">
                <img :src="icons.count" class="stat-icon-img" alt="count" />
                <div class="stat-val">{{ reportData.stageCount || 0 }}</div>
                <div class="stat-label">答题数</div>
              </div>
              <div class="stat-col">
                <img :src="icons.accuracy" class="stat-icon-img" alt="rate" />
                <div class="stat-val">{{ reportData.rightRate || 0 }}<span class="unit">%</span></div>
                <div class="stat-label">准确率</div>
              </div>
          </div>

          <!-- Pass Details -->
          <div class="section pass-details" v-if="reportData.stages && reportData.stages.length > 0">
            <div class="section-header">
              <div class="section-title brand-color">今日通关详情</div>
            </div>
            <div class="pass-list">
              <div
                v-for="(stage, index) in displayedStages"
                :key="index"
                class="pass-item"
              >
                <div class="pass-info">
                  <div class="chapter">第{{ stage.stageNumber }}关</div>
                  <div class="name">{{ stage.stageDescribe }}</div>
                </div>
                <div class="pass-status status-pass">通关{{ stage.attemptCount }}次</div>
              </div>

               <div class="pass-more" v-if="reportData.stages.length > 3" @click="showAllStages = !showAllStages">
                 {{ showAllStages ? '收起' : '更多' }} >
               </div>
            </div>
          </div>

          <!-- Today's Learning Points -->
          <div class="section learning-points" v-if="diagnosis.mastered.length > 0 || diagnosis.weak.length > 0">
             <div class="section-header">
               <div class="section-title brand-color">今日学习点</div>
             </div>

             <div class="learning-content">
               <div class="learning-item" v-if="diagnosis.mastered.length > 0">
                 <div class="item-title">
                   <img :src="icons.diagnosisEasy" class="item-icon" />
                   已掌握学习点
                 </div>
                 <div class="item-desc" v-for="(item, idx) in diagnosis.mastered" :key="'m'+idx">
                   <span class="bullet">·</span> <span v-html="formatDiagnosisText(item)"></span>
                 </div>
               </div>

               <div class="learning-item mt-heavy" v-if="showWeakPoints && diagnosis.weak.length > 0">
                 <div class="item-title">
                   <img :src="icons.muscle" class="item-icon" />
                   需要加强
                 </div>
                 <div class="item-desc" v-for="(item, idx) in diagnosis.weak" :key="'w'+idx">
                   <span class="bullet-red">·</span> <span v-html="formatDiagnosisText(item)"></span>
                 </div>
               </div>

               <div class="learning-more" v-if="diagnosis.weak.length > 0" @click="showWeakPoints = !showWeakPoints">
                 {{ showWeakPoints ? '收起' : '更多' }} >
               </div>
             </div>
          </div>

          <!-- Learning Diagnosis：右上角缺角处放 mascot_head -->
          <div class="section diagnosis" v-if="diagnosis.learningDiagnosis">
             <div class="diagnosis-mascot-slot">
               <img :src="icons.mascotHead" class="mascot-head" alt="mascot" />
             </div>
             <div class="diagnosis-header">
               <div class="section-title brand-color">kuku分析</div>
             </div>
             <div class="diagnosis-text" v-html="formatDiagnosisText(diagnosis.learningDiagnosis)"></div>
          </div>

          <!-- Quote -->
          <div class="section quote-section" v-if="reportData.encouragingWords">
            <div class="quote-icon">“</div>
            <div class="quote-text">
              {{ reportData.encouragingWords }}
            </div>
          </div>

          <!-- Bottom Button -->
          <div class="bottom-btn-wrapper">
             <div class="bottom-btn" @click="handleOpenApp">
              打开app查看
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { homeImages } from '../homeImages'

// Import assets
import logo from '@/assets/logo.svg'
import iconTime from '@/assets/day/icon_time.svg'
import iconCount from '@/assets/day/icon_count.svg'
import iconAccuracy from '@/assets/day/icon_accuracy.svg'
import iconDiagnosisEasy from '@/assets/day/icon_diagnosis_easy.svg'
import iconMuscle from '@/assets/day/icon_muscle.svg'
import mascotHead from '@/assets/day/mascot_head.svg'
import iconSpark from '@/assets/day/icon_streak_flame.svg'
import iconStreakFlame from '@/assets/day/icon_spark.svg'
import { openApp } from '@/utils/openApp'

export default {
  name: 'HomeDayReport',
  props: {
    query: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      images: homeImages,
      icons: {
        logo,
        time: iconTime,
        count: iconCount,
        accuracy: iconAccuracy,
        diagnosisEasy: iconDiagnosisEasy,
        muscle: iconMuscle,
        mascotHead: mascotHead,
        spark: iconSpark,
        streakFlame: iconStreakFlame
      },
      loading: true,
      error: null,
      reportData: {},
      diagnosis: {
        learningDiagnosis: '',
        mastered: [],
        weak: []
      },
      showAllStages: false,
      showWeakPoints: false
    }
  },
  computed: {
    reportId() {
      return this.query.id || (this.$route && this.$route.query && this.$route.query.id) || null
    },
    displayedStages() {
      if (!this.reportData.stages) return []
      return this.showAllStages ? this.reportData.stages : this.reportData.stages.slice(0, 3)
    }
  },
  mounted() {
    this.fetchDayReport()
  },
  methods: {
    async fetchDayReport() {
      if (!this.reportId) {
        this.error = '缺少报告ID参数'
        this.loading = false
        return
      }

      this.loading = true
      this.error = null

      try {
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL || ''}/share/getDayReport?id=${this.reportId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        })
        const result = await response.json()

        if (result.code === 200 && result.data) {
          this.reportData = result.data
          this.parseDiagnosis(result.data.studyDiagnosis)
        } else {
          this.error = result.message || '获取数据失败'
        }
      } catch (err) {
        console.error('获取日报失败:', err)
        this.error = '网络错误，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    parseDiagnosis(jsonStr) {
      if (!jsonStr) return

      try {
        // Parse the JSON string
        const diagObj = JSON.parse(jsonStr)

        // Extract learningDiagnosis text
        this.diagnosis.learningDiagnosis = diagObj.learningDiagnosis || ''

        // Extract mastered and weak arrays (they are already arrays in the new format)
        this.diagnosis.mastered = Array.isArray(diagObj.mastered) ? diagObj.mastered : []
        this.diagnosis.weak = Array.isArray(diagObj.weak) ? diagObj.weak : []

      } catch (e) {
        console.error('解析诊断数据失败:', e)
      }
    },
    formatDiagnosisText(text) {
      if (!text) return ''
      // 先转义 HTML 特殊字符，防止 XSS
      let html = String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      // 处理 **加粗**
      html = html.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
      // 处理颜色标签 {{文字|颜色}}
      html = html.replace(/\{\{(.+?)\|([^}]+)\}\}/g, function (_, text, color) {
        var safe = String(color).trim()
        if (/^(#[0-9a-fA-F]{3,8}|rgb\([^)]+\)|rgba\([^)]+\)|[a-zA-Z]+)$/.test(safe)) {
          return '<span style="color:' + safe + '">' + text + '</span>'
        }
        return text
      })
      // 处理无序列表
      const lines = html.split('\n')
      let result = []
      let inList = false
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        const listMatch = line.match(/^\s*[-*]\s+(.*)$/)
        if (listMatch) {
          if (!inList) {
            result.push('<ul class="diag-list">')
            inList = true
          }
          result.push('<li>' + listMatch[1] + '</li>')
        } else {
          if (inList) {
            result.push('</ul>')
            inList = false
          }
          const indented = line.replace(/^(\s+)/, function (m) {
            return new Array(m.length + 1).join('&nbsp;')
          })
          result.push(indented)
        }
      }
      if (inList) result.push('</ul>')
      html = result.join('\n')
      html = html.replace(/\n/g, '<br/>')
      html = html.replace(/(<\/ul>)<br\/>/g, '$1')
      html = html.replace(/<br\/>(<li>)/g, '$1')
      return html
    },
    handleOpenApp() {
      openApp()
    }
  }
}
</script>

<style lang="scss" scoped>
/* Page Wrapper */
.page-day-report {
  min-height: 100vh;
  background: #F5F5F5;
  padding-bottom: 30px;
  padding-top: calc(56px + env(safe-area-inset-top, 0px)); /* 为固定头部留出空间 */
  position: relative;
  overflow-x: hidden;
  width: 100%;
}

.phone {
  padding: 0 12px 12px;
}

.section {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 12px;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin-bottom: 4px;

  &.brand-color {
    color: #F15B5B;
  }
}

/* Custom Top Bar：固定顶部，不随页面滚动 */
.custom-top-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 20px 12px 20px;
  background: #fff;
}

.brand-area {
  display: flex;
  align-items: center;

  .brand-logo {
     width: 32px; height: 32px;
     margin-right: 8px;
  }
  .brand-text {
     font-size: 18px;
     font-weight: 700;
     color: #f15b5b;
  }
}

.btn-download {
  background: #f15b5b;
  color: #fff;
  border: none;
  border-radius: 99px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
}

/* Header Background Layer - Yellow Gradient（头部已固定，背景从内容区顶部开始） */
.header-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 280px;
  background: linear-gradient(180deg, #FFB938 0%, #FFCE6B 60%, #F5F5F5 100%);
  z-index: 0;
}

/* Header Area Content */
.report-header {
  padding-top: 24px; /* Reduced space since top bar is separate */
  padding-bottom: 60px;
  color: #fff;
  position: relative;
  z-index: 1;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.header-item {
  flex: 1;
  display: flex;
  flex-direction: column;

  .label {
    font-size: 14px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    opacity: 0.95;
    font-weight: 500;

    .header-icon {
      width: 18px; /* Enlarged icon */
      height: 18px;
      margin-right: 6px;
    }
  }
  .value {
    font-size: 34px;
    font-weight: 700;
    line-height: 1.1;

    &.large {
      font-size: 40px; /* Larger font */
    }
  }
}

.divider-vertical {
  width: 1px;
  height: 48px;
  background: rgba(255,255,255,0.5);
  margin: 10px 24px 0;
}

/* Floating Stats Card */
.stats-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 24px 0;
  display: flex;
  justify-content: space-around;
  margin-top: -50px; /* Overlap with header */
  margin-bottom: 16px;
  position: relative;
  z-index: 2;
  box-shadow: 0 8px 24px rgba(255, 185, 56, 0.15); /* Softer, colored shadow */
  margin-left: 4px;
  margin-right: 4px;
}

.stat-col {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-icon-img {
  width: 44px; /* Larger icons */
  height: 44px;
  margin-bottom: 8px;
}

.stat-val {
  font-size: 20px;
  font-weight: 800;
  color: #F37D30;
  line-height: 1.2;

  .unit {
    font-size: 13px;
    font-weight: 600;
    margin-left: 2px;
    vertical-align: 1px;
    color: #F37D30;
  }
}

.stat-label {
  font-size: 12px;
  color: #333;
  margin-top: 4px;
  font-weight: 600;
}

/* Pass Details */
.pass-details {
  .section-header {
    margin-bottom: 16px;
  }
}

.pass-list {
  display: flex;
  flex-direction: column;
}

.pass-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  padding: 16px 0;
  border-bottom: 1px solid #F0F0F0;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }

  &.active-item {
     display: block;

     .pass-row {
       display: flex;
       justify-content: space-between;
       align-items: center;
     }

     .blue-line {
       height: 4px;
       background: #1890FF;
       width: 100%;
       border-radius: 2px;
       margin-top: 12px;
     }
  }
}

.pass-info {
  .chapter {
    font-size: 12px;
    color: #999;
    margin-bottom: 6px;
  }
  .name {
    font-size: 15px;
    font-weight: 700;
    color: #333;
  }
}

.pass-status {
  font-size: 13px;
  font-weight: 600;

  &.status-perfect {
    color: #F15B5B;
  }
  &.status-pass {
    color: #FFC107;
  }
}

.pass-more {
  text-align: center;
  font-size: 13px;
  color: #999;
  margin-top: 8px;
  background: #F2F2F2;
  padding: 12px;
  border-radius: 24px; /* More rounded */
  cursor: pointer;
  font-weight: 500;
}

/* Learning Points */
.learning-points {
  padding: 20px;
}

.learning-content {
  .learning-item {
    margin-bottom: 16px;

    &.mt-heavy {
      margin-top: 28px;
    }

    .item-title {
      font-size: 15px;
      font-weight: 700;
      color: #333;
      margin-bottom: 10px;
      display: flex;
      align-items: center;

      .item-icon {
        width: 24px;
        height: 24px;
        margin-right: 8px;
      }
    }

    .item-desc {
      font-size: 13px;
      color: #333;
      line-height: 1.8;
      padding-left: 10px;
      display: flex;
      align-items: flex-start;
      margin-bottom: 4px;

      .bullet {
        color: #F15B5B;
        margin-right: 8px;
        font-weight: 900;
      }

      .bullet-red {
        color: #F15B5B;
        margin-right: 8px;
        font-weight: 900;
      }
    }
  }

  .learning-more {
    text-align: center;
    font-size: 13px;
    color: #999;
    margin-top: 8px;
    background: #F2F2F2;
    padding: 12px;
    border-radius: 24px; /* More rounded */
    cursor: pointer;
    font-weight: 500;
  }
}

/* Diagnosis - 右上角圆形缺角 + mascot_head */
.diagnosis {
  position: relative;
  overflow: visible;
  padding-top: 24px;
}

/* 用与页面背景同色的圆形伪元素，盖在卡片右上角制造"缺角"效果 */
.diagnosis::before {
  content: '';
  position: absolute;
  width: 54px;
  height: 54px;
  top: -9px;
  right: -9px;
  border-radius: 50%;
  background: #F5F5F5;
  z-index: 1;
}

.diagnosis-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  position: relative;
}

/* mascot 放在缺角圆心处，z-index 高于灰色圆 */
.diagnosis-mascot-slot {
  position: absolute;
  top: -9px;
  right: -9px;
  width: 54px;
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.mascot-head {
  width: 48px;
  height: 48px;
  object-fit: contain;
}

.diagnosis-text {
  font-size: 13px;
  color: #666;
  line-height: 1.8;
  padding-right: 50px;
  text-align: justify;

  ::v-deep .diag-list {
    margin: 6px 0;
    padding-left: 0;
    list-style: none;

    li {
      position: relative;
      padding-left: 14px;
      margin-bottom: 6px;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 8px;
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: #F15B5B;
      }
    }
  }
}

/* Quote */
.quote-section {
  position: relative;
  padding: 24px 24px 24px 48px;

  .quote-icon {
    position: absolute;
    top: 20px;
    left: 20px;
    font-size: 40px;
    color: #E0E0E0;
    font-family: sans-serif;
    font-weight: 900;
    line-height: 1;
  }

  .quote-text {
    font-size: 13px;
    color: #999;
    line-height: 1.6;
    letter-spacing: 0.5px;
  }
}

/* Bottom Button */
.bottom-btn-wrapper {
  margin-top: 20px;
}
.bottom-btn {
  width: 100%;
  height: 52px;
  line-height: 52px;
  background: #FFB32F;
  color: #fff;
  text-align: center;
  border-radius: 26px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(255, 179, 47, 0.35);

  &:active {
    opacity: 0.9;
    transform: scale(0.98);
  }
}

// Loading State
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f0f0f0;
  border-top-color: #f15b5b;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 16px;
  font-size: 14px;
  color: #999;
}

// Error State
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.error-text {
  font-size: 14px;
  color: #f15b5b;
  margin-bottom: 16px;
}

.btn-retry {
  background: #f15b5b;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:active {
    opacity: 0.8;
  }
}
</style>
